import { FORMAT_VERSION } from './constants.js';

export class FormatVersionError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'FormatVersionError';
  }
}

type Migration = (file: Record<string, unknown>) => Record<string, unknown>;

/**
 * Migrations from version N to N + 1, keyed by N. Each one receives a copy and
 * returns the upgraded file. Add an entry here whenever FORMAT_VERSION goes up,
 * and publish the new schema beside the old one.
 */
const MIGRATIONS: Record<number, Migration> = {};

/**
 * Brings a project, scene or prefab file up to the current format version.
 * Throws for files with no version, or from a newer release of animo-editor.
 */
export function migrate<T>(file: unknown): { file: T; from: number; migrated: boolean } {
  if (typeof file !== 'object' || file === null || Array.isArray(file)) {
    throw new FormatVersionError('File is not a JSON object');
  }
  const version = (file as { formatVersion?: unknown }).formatVersion;
  if (typeof version !== 'number' || !Number.isInteger(version) || version < 1) {
    throw new FormatVersionError('File has no valid formatVersion');
  }
  if (version > FORMAT_VERSION) {
    throw new FormatVersionError(
      `File uses formatVersion ${version}, but this version of animo-editor reads up to ${FORMAT_VERSION}. Update animo-editor.`,
    );
  }
  let current = file as Record<string, unknown>;
  for (let v = version; v < FORMAT_VERSION; v++) {
    const step = MIGRATIONS[v];
    if (!step) throw new FormatVersionError(`No migration from formatVersion ${v}`);
    current = { ...step(structuredClone(current)), formatVersion: v + 1 };
  }
  return { file: current as T, from: version, migrated: version !== FORMAT_VERSION };
}
